import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, mediaUrl } from "@/lib/oraya-api";
import { Seo, Reveal, Overline } from "@/components/site/primitives";
import { OButton } from "@/components/site/OButton";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" }); }
  catch { return ""; }
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/insights/${slug}`).then(({ data }) => setA(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 oraya-container text-center" data-testid="insight-notfound">
        <h1 className="display text-4xl text-ink">Article not found</h1>
        <div className="mt-8"><OButton to="/insights" variant="secondary">Back to insights</OButton></div>
      </div>
    );
  }
  if (!a) return <div className="pt-40 pb-32 oraya-container text-subtle">Loading…</div>;

  const ld = {
    "@context": "https://schema.org", "@type": "Article", headline: a.title,
    description: a.seo_description || a.description, author: { "@type": "Organization", name: a.author || "ORAYA" },
    datePublished: a.publish_date, publisher: { "@type": "Organization", name: "ORAYA" },
  };

  return (
    <div className="pt-32 pb-32">
      <Seo title={`${a.seo_title || a.title} — ORAYA`} description={a.seo_description || a.description} path={`/insights/${a.slug}`} type="article" image={mediaUrl(a.cover_image)} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <article className="oraya-container max-w-3xl">
        <Link to="/insights" className="inline-flex items-center gap-2 text-subtle hover:text-ink transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /> All insights
        </Link>
        <Reveal>
          <div className="flex items-center gap-3 text-xs text-subtle">
            <span className="uppercase tracking-[0.15em]">{a.category}</span><span>·</span>
            <span>{fmtDate(a.publish_date)}</span><span>·</span><span>{a.author}</span>
          </div>
          <h1 className="display text-4xl sm:text-5xl font-light tracking-tight text-ink mt-5 leading-[1.08]">{a.title}</h1>
          <p className="text-xl text-subtle mt-5">{a.description}</p>
        </Reveal>
        {mediaUrl(a.cover_image) && (
          <Reveal delay={0.1}>
            <img src={mediaUrl(a.cover_image)} alt={a.title} className="w-full rounded-3xl mt-10 object-cover max-h-[460px]" loading="lazy" />
          </Reveal>
        )}
        <Reveal delay={0.15}>
          <div className="prose prose-invert max-w-none mt-12 text-subtle text-lg leading-relaxed whitespace-pre-line">
            {a.content}
          </div>
        </Reveal>
      </article>
    </div>
  );
}
